import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextProduct } from './Product';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProductDA } from '../../../../services/admin/product.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Message } from '../../../../models/message.model';
import { Product } from '../../../../models/product.model';
import { Feature } from '../../../../models/feature.model';
import { legendInvalid, legendIconInvalid } from '../../../../utils/legend.data.util';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { uploadUrl } from '../../../../scripts/file.load.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { suffixLegend } from '../../../../scripts/name.legend.script';

import AppProductForm from './component/ProductForm';
import AppPreloader from '../../../../components/Preloader';

export interface ProductUpdatePageProps {};

let errorResponse: Error, messageResponse: Message, productResponse: Product;

const ProductUpdatePage: React.FunctionComponent<ProductUpdatePageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, loadProductsForCompany} = UseOutletContextProduct()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [names, setNames] = useState({value: {en: '', fr: '', it: '', de: ''}, valid: {en: false, fr: false, it: false, de: false}})
  const [brand, setBrand] = useState({value: '', valid: false})
  const [category, setCategory] = useState({value: '', valid: false, object: null as any})
  const [tags, setTags] = useState({value: [] as string[], valid: false})
  const [accounts, setAccounts] = useState({value: {expense: '', vat: '', income: ''}, valid: {expense: false, vat: false, income: false}})
  const [variants, setVariants] = useState({value: [] as {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: [] as {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]})
  const [variantDefault, setVariantDefault] = useState({value: {id: '', description_short: '', description_long: '', code: '', sku: '', codebar: '', price_sale: '', price_purchase: '', tax_code_sale: '', tax_code_purchase: '', tax_rate_sale: 0, tax_rate_purchase: 0, unit: '', discount: {type: 'none', value: '', quantity: ''}, photos: [] as {name: string, size: number, type: string, view: string, file: any}[], features: [] as {id_feature: string, name: string, value: string}[]}, valid: {description_short: false, description_long: true, code: true, sku: true, codebar: true, price_sale: false, price_purchase: false, tax_sale: false, tax_purchase: false, unit: false, discount: {type: true, value: true, quantity: true}, photos: [] as {file: boolean, message: string}[], features: [] as boolean[], gallery: true}})
  const [product, setProduct] = useState<Product | null>(null)
  const [list, setList] = useState([] as Feature[])

  const loadProduct = async (id_company: string, id_product: string) => {
    await ProductDA.getProduct(id_company, id_product).then( (response) => {
      if (response.status === 200) {
        productResponse = response.data?.data ?? response.data
        if (productResponse.variants_data) {
          productResponse.variants = productResponse.variants_data
        }

        let tempVariantsValue: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[] = []
        let tempVariantsValid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[] = []

        for (let variant of productResponse.variants) {
          let newVariantValue: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]} = {
            id: variant.id ?? '',
            description_short: variant.description_short ?? '',
            description_long: variant.description_long ?? '',
            code: variant.code ?? '',
            sku: variant.sku ?? '',
            codebar: variant.codebar ?? '',
            price_sale: (variant.price_sale?.amount ?? '').toString(),
            price_purchase: (variant.price_purchase?.amount ?? '').toString(),
            tax_code_sale: variant.price_sale?.tax_code ?? '',
            tax_code_purchase: variant.price_purchase?.tax_code ?? '',
            tax_rate_sale: variant.price_sale?.tax_rate ?? 0,
            tax_rate_purchase: variant.price_purchase?.tax_rate ?? 0,
            unit: typeof variant.unit === 'object' ? (variant.unit?.id ?? '') : (variant.unit ?? ''),
            discount: {
              type: variant.discount?.type ?? 'none',
              value: (variant.discount?.value ?? '').toString(),
              quantity: (variant.discount?.quantity ?? '').toString()
            },
            photos: [],
            features: []
          }
          let newVarianttValid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean} = {
            description_short: true,
            description_long: true,
            code: true,
            sku: true,
            codebar: true,
            price_sale: true,
            price_purchase: true,
            tax_sale: true,
            tax_purchase: true,
            unit: true,
            discount: {
              type: true,
              value: true,
              quantity: true
            },
            photos: [],
            features: [],
            gallery: true
          }

          for (let photo of (variant.photos ?? [])) {
            newVariantValue.photos.push(uploadUrl(lang, photo).value)
            newVarianttValid.photos.push(uploadUrl(lang, photo).valid)
          }

          for (let feature of (variant.features ?? [])) {
            newVariantValue.features.push(feature)
            newVarianttValid.features.push(true)
          }

          tempVariantsValue.push(newVariantValue)
          tempVariantsValid.push(newVarianttValid)
        }

        if (tempVariantsValue.length > 1 && tempVariantsValid.length > 1) {
          setVariants({value: tempVariantsValue, valid: tempVariantsValid})
        } else {
          setVariantDefault({value: tempVariantsValue[0], valid: tempVariantsValid[0]})
        }

        setProduct(productResponse)
        setName({value: productResponse.display_name, valid: true})
        setNames({value: productResponse.names, valid: {en: true, fr: true, it: true, de: true}})
        setAccounts({value: productResponse.accounts ?? {expense: productResponse.expense_account_id ?? '', vat: productResponse.tva_account_id ?? '', income: productResponse.product_account_id ?? ''}, valid: {expense: true, vat: true, income: true}})
        setCategory({value: typeof productResponse.category === 'object' ? (productResponse.category?.id ?? '') : (productResponse.category ?? ''), valid: true, object: typeof productResponse.category === 'object' ? productResponse.category : null})
        setBrand({value: typeof productResponse.brand === 'object' ? (productResponse.brand?.id ?? '') : (productResponse.brand ?? ''), valid: true})
        setTags({value: productResponse.tags, valid: true})
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const resolveUnit = (unitId: string) => {
    const mu = settingsForCompany?.measure_units?.find((m: any) => m.id === unitId)
    return mu ? { id: mu.id, name: mu.name, type: mu.type ?? 'product', symbol: mu.symbol ?? '' } : unitId
  }

  const submitProductUpdate = async () => {
    setLoadIndicator('on')

    let namesIsValid: boolean = isValidObjectAttributes(names.valid)
    let accountsIsValid: boolean = isValidObjectAttributes(accounts.valid)
    let variantsIsValid: boolean = (variants.valid.length < 2 && variants.valid.length < 2) ? isValidObjectAttributes(variantDefault.valid) : variants.valid.every(item => isValidObjectAttributes(item))

    if (companyForUser && product && name.valid && namesIsValid && accountsIsValid && brand.valid && category.valid && tags.valid && variantsIsValid) {
      let variantList = (variants.valid.length < 2 && variants.valid.length < 2)
        ?
        [ {...variantDefault.value, unit: resolveUnit(variantDefault.value.unit), photos: variantDefault.value.photos.map((item) => (item.file))} ]
        :
        variants.value.map((item) => { return {
          id: item.id,
          description_short: item.description_short,
          description_long: item.description_long,
          code: item.code,
          sku: item.sku,
          codebar: item.codebar,
          price_sale: item.price_sale,
          price_purchase: item.price_purchase,
          tax_code_sale: item.tax_code_sale,
          tax_code_purchase: item.tax_code_purchase,
          tax_rate_sale: item.tax_rate_sale,
          tax_rate_purchase: item.tax_rate_purchase,
          unit: resolveUnit(item.unit),
          discount: {
            type: item.discount.type,
            value: item.discount.value,
            quantity: item.discount.quantity
          },
          features: item.features,
          photos: item.photos.map((sub_item) => (sub_item.file))
        }})

      await ProductDA.putProduct(companyForUser.id, product.id, name.value, category.value, brand.value, names.value, accounts.value, tags.value, variantList).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data
          loadProductsForCompany(companyForUser.id)

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate(-1)
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value) legendIconInvalid('input-name', 'legend-name-required')
        if (!name.value) legendIconInvalid('input-name-other', 'legend-name-other-required')
        if (!category.value) legendInvalid('legend-category-required')
        if (!accounts.value.expense) legendInvalid('legend-account-expense-required')
        if (!accounts.value.vat) legendInvalid('legend-account-vat-required')
        if (!accounts.value.income) legendInvalid('legend-account-income-required')
        if (variants.valid.length === 0 && variants.valid.length === 0) {
          if (!variantDefault.value.description_short) legendIconInvalid('input-variant-description-short', 'legend-variant-description-short-required')
          if (!variantDefault.value.unit) legendInvalid('legend-variant-unit-required')
          if (!variantDefault.value.price_sale) legendIconInvalid('input-variant-price-sale', 'legend-variant-price-sale-required')
          if (!variantDefault.value.price_purchase) legendIconInvalid('input-variant-price-purchase', 'legend-variant-price-purchase-required')
          if (!variantDefault.value.tax_code_sale) legendInvalid('legend-variant-tax-sale-required')
          if (!variantDefault.value.tax_code_purchase) legendInvalid('legend-variant-tax-purchase-required')
          if (variantDefault.value.discount.type !== 'none') {
            if (!variantDefault.value.discount.value) legendIconInvalid('input-variant-discount-value', 'legend-variant-discount-value-required')
            if (!variantDefault.value.discount.quantity) legendIconInvalid('input-variant-discount-quantity', 'legend-variant-discount-quantity-required')
          }
          for (let i = 0; i < variantDefault.value.features.length; i++) {
            if (!variantDefault.value.features[i].value) legendInvalid(`legend-variant-feature-required${suffixLegend(i)}`)
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadProduct(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.products, branch: lang.labels.updateProduct}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && categoriesForCompany && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingIncomeAccountsForCompany && product
        ?
        <AppProductForm action={"update"} loadIndicator={loadIndicator} product={product} name={name} category={category} brand={brand} tags={tags} names={names} accounts={accounts} variantDefault={variantDefault} variants={variants} listFeatures={list} setName={setName} setCategory={setCategory} setBrand={setBrand} setTags={setTags} setNames={setNames} setAccounts={setAccounts} setVariantDefault={setVariantDefault} setVariants={setVariants} setListFeatures={setList} submitProduct={submitProductUpdate}></AppProductForm>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default ProductUpdatePage;
